import React, {
  useState,
  useEffect,
  Fragment,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  useTheme,
  Fade,
  Popper,
  Paper,
  Typography,
  Grid,
  Modal,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";

import Topbar from "../../../../../components/topbar/TopBar";
import Header from "../../../../../components/header/Header";
import {
  getBookingNow,
  getBookingNowV2,
  getBookingRiderFilters,
  updateBookingTravelStatus,
  updateBookingNowSubRoutes,
  updatePaymentBookingNow,
  updateStatusBooking,
} from "../../../../../api/booking/bookingNow";
import SnackBar from "../../../../../components/snackbar/SnackBar";
import { convertDate } from "../../../../../helpers/date/convertDate";
import { tokens } from "../../../../../theme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../../components/sidebar/Sidebar";
import {
  decodeAdminToken,
  getToken,
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../../../helpers/token/refreshToken";
import { getDriverAndVehicle } from "../../../../../api/users/driver/driver";
import { getClients } from "../../../../../api/users/client/client";
import {
  Document,
  PDFDownloadLink,
  Page,
  StyleSheet,
} from "@react-pdf/renderer";
import { DataGrid, esES } from "@mui/x-data-grid";
import DocumentName from "./pdf/now/elements/DocumentName";
import TableBooking from "./pdf/now/elements/TableBooking";
import Contact from "../../vehicle/pdf/elements/Contact";
import ReportTitle from "../../employee/report/elements/Title";
import ReportDocumentName from "../../employee/report/elements/DocumentName";
import ReportContact from "../../employee/report/elements/Contact";
import ReportTableHorizontal from "../../employee/report/elements/TableHorizontal";
import {
  createAdvanceCash,
  getAdvanceCashOrder,
} from "../../../../../api/booking/advance";
import { dateFormat, timeFormat } from "../../../../../helpers/date/date";
import HeaderM from "./pdf/later/elements/HeaderM";
import VoucherHistory from "../../../../../components/datatable/voucherfinal/Voucher";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { mapBookingRiderFilterResult } from "../../../../../data/booking/bookingNowMapper";

const PAGE_SIZE_OPTIONS = [50, 100];
const INITIAL_RANGE = { start: 0, end: 100 };
const MAX_SUB_ROUTES_DIALOG_ITEMS = 10;
const getRadioOptionSx = (selectedValue, optionValue) => ({
  border: "1px solid",
  borderColor: selectedValue === optionValue ? "secondary.main" : "divider",
  borderRadius: "8px",
  p: { xs: "1px 4px 1px 1px", sm: "2px 8px 2px 2px" },
  m: 0,
  minWidth: 0,
  ".MuiRadio-root": {
    p: { xs: "2px", sm: "6px" },
  },
  ".MuiFormControlLabel-label": {
    fontSize: { xs: 11, sm: 14 },
    fontWeight: selectedValue === optionValue ? 700 : 400,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
});

const centeredRadioGroupSx = {
  justifyContent: "center",
  gap: { xs: 0.5, sm: 1 },
};

const headerActionButtonSx = {
  height: { xs: 34, sm: 38, md: 40 },
  minWidth: { xs: 76, sm: 84 },
  px: { xs: 0.75, sm: 1.5 },
  fontSize: { xs: 10, sm: 12, md: 13 },
  lineHeight: 1,
  whiteSpace: "nowrap",
};

const headerPdfLinkSx = {
  display: "flex",
  textDecoration: "none",
  flexShrink: 0,
};

function isOverflown(element) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
  const { width, value } = props;
  const wrapper = React.useRef(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
        setShowFullCell(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: "center",
        lineHeight: "24px",
        width: 1,
        height: 1,
        position: "relative",
        display: "flex",
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: 1,
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Box>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{ width, marginLeft: -17 }}
        >
          <Paper
            elevation={1}
            style={{ minHeight: wrapper.current.offsetHeight - 3 }}
          >
            <Typography variant="body2" style={{ padding: 8 }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

function getSubRoutesList(subRoutes) {
  if (!subRoutes) return [];

  try {
    const routes =
      typeof subRoutes === "string" ? JSON.parse(subRoutes) : subRoutes;
    const routeList = Array.isArray(routes) ? routes : Object.values(routes);

    return routeList
      .filter((subRoute) => subRoute?.name)
      .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  } catch (error) {
    return [];
  }
}

function getDestinationText(item) {
  const subRoutes = getSubRoutesList(item.sub_routes);

  if (subRoutes.length === 0) return item.destination_name;

  const destinationRoute = {
    id: subRoutes.length + 1,
    name: item.destination_name,
  };
  const routes = [...subRoutes, destinationRoute].filter(
    (route) => route?.name,
  );

  return routes.map((route, index) => `${index + 1}. ${route.name}`).join("\n");
}

function getRoutePoint(route) {
  if (!route) return "";

  const lat = route.lat ?? route.latitude;
  const lng = route.lng ?? route.longitude;

  if (lat && lng) return `${lat},${lng}`;
  return route.name || "";
}

function getGoogleMapsRouteUrl(origin, subRoutes, destination) {
  const waypointPoints = getSubRoutesList(subRoutes)
    .map(getRoutePoint)
    .filter(Boolean);
  const params = new URLSearchParams({
    api: "1",
    origin: getRoutePoint(origin),
    destination: getRoutePoint(destination),
  });

  if (waypointPoints.length > 0) {
    params.set("waypoints", waypointPoints.join("|"));
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function openGoogleMapsRoute(origin, subRoutes, destination) {
  window.open(
    getGoogleMapsRouteUrl(origin, subRoutes, destination),
    "_blank",
    "noreferrer",
  );
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function getRouteId(route, fallback = "") {
  return String(
    route?.routeId ||
      `${route?.id ?? fallback}-${route?.name ?? ""}-${route?.lat ?? ""}-${
        route?.lng ?? ""
      }`,
  );
}

function createDialogRoute(route, fallback = "") {
  const lat = route?.lat ?? route?.latitude ?? route?.destination_lat ?? "";
  const lng = route?.lng ?? route?.longitude ?? route?.destination_lng ?? "";
  const name = route?.name ?? route?.destination_name ?? route?.label ?? "";

  return {
    ...route,
    id: route?.id ?? fallback,
    name,
    lat,
    lng,
    routeId: getRouteId({ ...route, name, lat, lng }, fallback),
  };
}

function getNormalizedRouteName(route) {
  return String(route?.name || "")
    .trim()
    .toLowerCase();
}

function SubRouteSortableItem({ route, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(route.routeId),
    index,
    group: "history-now-sub-routes",
  });
  const hasCoordinates = route.lat && route.lng;
  const content = (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flex: 1 }}>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {route.name}
      </Typography>
      <LocationOnIcon fontSize="small" color="error" />
    </Stack>
  );

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      {hasCoordinates ? (
        <a
          href={`https://maps.google.com/?q=${route.lat},${route.lng}`}
          target="_blank"
          rel="noreferrer"
          style={{
            color: "inherit",
            display: "flex",
            flex: 1,
            textDecoration: "none",
          }}
        >
          {content}
        </a>
      ) : (
        content
      )}
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar paradero"
        onClick={() => onDelete(route.routeId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const HistoryListBookingNow = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const dataToken = getToken("adttkn");
  const { company_id } = decodeAdminToken(dataToken);

  const [booking, setBooking] = useState([]);
  const [UID, setUID] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [hasMoreBookings, setHasMoreBookings] = useState(true);
  const [subRoutesDialogOpen, setSubRoutesDialogOpen] = useState(false);
  const [subRoutesDialogBookingId, setSubRoutesDialogBookingId] =
    useState(null);
  const [subRoutesDialogLists, setSubRoutesDialogLists] = useState({});
  const [subRouteInput, setSubRouteInput] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterOption, setFilterOption] = useState("numeroPerdido");
  const [filterTextValue, setFilterTextValue] = useState("");
  const [filterDateMode, setFilterDateMode] = useState("fecha");
  const [filterRouteMode, setFilterRouteMode] = useState("registrado");
  const [filterNotesMode, setFilterNotesMode] = useState("general");
  const [filterDriverMode, setFilterDriverMode] = useState("nombre");
  const [filterDateStartValue, setFilterDateStartValue] = useState(dayjs());
  const [filterDateEndValue, setFilterDateEndValue] = useState(dayjs());
  const [filterAutocompleteValue, setFilterAutocompleteValue] = useState(null);
  const [filterNoResults, setFilterNoResults] = useState(false);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState(null);
  const [driverSelect, setDriverSelect] = useState(null);
  const [advance, setAdvance] = useState(0);
  const [otherPay, setOtherPay] = useState(false);
  const [datePay, setDatePay] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenFilterDialog = () => {
    setFilterNoResults(false);
    setFilterDialogOpen(true);
  };
  const handleCloseFilterDialog = () => setFilterDialogOpen(false);

  const handleFilterOptionChange = (event) => {
    setFilterOption(event.target.value);
    setFilterTextValue("");
    setFilterDateMode("fecha");
    setFilterRouteMode("registrado");
    setFilterNotesMode("general");
    setFilterDriverMode("nombre");
    setFilterAutocompleteValue(null);
    setFilterNoResults(false);
  };

  const travelStatus = {
    1: "ASIGNADO",
    2: "EN RUTA",
    3: "FINALIZADO",
  };

  const filterAutocompleteOptions = useMemo(() => {
    if (filterOption === "cliente") {
      return clients;
    }

    if (filterOption === "conductor" && filterDriverMode === "nombre") {
      const driverOptionsByName = new Map();

      (drivers || []).forEach((driver) => {
        const driverName = `${driver?.name ?? ""} ${
          driver?.lastname ?? ""
        }`.trim();
        if (!driverName || driverOptionsByName.has(driverName)) return;
        driverOptionsByName.set(driverName, driver);
      });

      return Array.from(driverOptionsByName.values());
    }

    const fieldByOption = {
      origen: "origin_name",
      destino: "destination_name",
    };
    const field = fieldByOption[filterOption];

    if (!field) return [];

    return [...new Set(booking.map((item) => item?.[field]).filter(Boolean))];
  }, [booking, clients, drivers, filterDriverMode, filterOption]);

  const filterInputLabel = {
    numeroPerdido: "Numero Perdido",
    origen: "Origen",
    destino: "Destino",
    notas: "Notas",
    fecha: "Fecha",
    cliente: "Cliente",
    conductor: "Conductor",
  };

  const showFilterTextField = filterOption === "numeroPerdido";
  const showFilterDatePicker = filterOption === "fecha";
  const showFilterAutocomplete =
    filterOption === "origen" ||
    filterOption === "destino" ||
    filterOption === "cliente" ||
    filterOption === "conductor";
  const showRouteFilterMode =
    filterOption === "origen" || filterOption === "destino";
  const showNotesFilterMode = filterOption === "notas";
  const showDriverFilterMode = filterOption === "conductor";

  const getFilterPayload = () => {
    const textValue = filterTextValue.trim();
    const autocompleteValue =
      typeof filterAutocompleteValue === "string"
        ? filterAutocompleteValue.trim()
        : "";
    const routeValue =
      filterRouteMode === "libre" ? textValue : autocompleteValue;

    if (filterOption === "numeroPerdido" && textValue) {
      return { order_number: textValue };
    }

    if (filterOption === "notas" && textValue) {
      return filterNotesMode === "admin"
        ? { notes_admin: textValue }
        : { note: textValue };
    }

    if (filterOption === "origen" && routeValue) {
      return { origin: routeValue };
    }

    if (filterOption === "destino" && routeValue) {
      return { destination: routeValue };
    }

    if (filterOption === "cliente" && filterAutocompleteValue?.id) {
      return { clientId: Number(filterAutocompleteValue.id) };
    }

    if (filterOption === "conductor") {
      if (filterDriverMode === "placa" && textValue) {
        return { plate: textValue };
      }

      if (filterAutocompleteValue?.driver_id || filterAutocompleteValue?.id) {
        return {
          drivers_id: Number(
            filterAutocompleteValue.driver_id ?? filterAutocompleteValue.id,
          ),
        };
      }
    }

    if (filterOption === "fecha" && filterDateStartValue?.isValid?.()) {
      if (filterDateMode === "fecha") {
        return { date: filterDateStartValue.format("YYYY-MM-DD") };
      }

      if (!filterDateEndValue?.isValid?.()) {
        return null;
      }

      return {
        dateStart: filterDateStartValue.format("YYYY-MM-DD"),
        dateEnd: filterDateEndValue.format("YYYY-MM-DD"),
      };
    }

    return null;
  };

  const handleSearchFilter = async () => {
    const filterPayload = getFilterPayload();

    if (!filterPayload) {
      snackBarDialog("INGRESE UN FILTRO PARA BUSCAR", "error");
      return;
    }

    try {
      setIsLoading(true);
      setFilterNoResults(false);
      const response = await getBookingRiderFilters(filterPayload);
      console.log("booking rider filters response", response);
      const responseData = response?.data?.data ?? response?.data ?? [];
      const responseBookings = Array.isArray(responseData)
        ? responseData
        : Object.values(responseData);

      if (responseBookings.length === 0) {
        setFilterNoResults(true);
        return;
      }

      setBooking(responseBookings.map(mapBookingRiderFilterResult));
      setDataSelected([]);
      setPage(0);
      setHasMoreBookings(false);
      handleCloseFilterDialog();
    } catch (error) {
      console.log("booking rider filters error", error);
      snackBarDialog("NO SE PUDO BUSCAR", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtherPay = (e) => {
    const isChecked = e.target.checked;
    setOtherPay(isChecked);
    if (isChecked) {
      const currentDate = new Date();
      setDatePay(`${dateFormat(currentDate)} ${timeFormat(currentDate)}`);
    } else setDatePay("");
  };

  const handleOpenSubRoutesDialog = (bookingId, subRoutes, destination) => {
    const routeList = [...subRoutes, destination]
      .filter((route) => route?.name)
      .map((route, index) => createDialogRoute(route, index + 1));

    setSubRoutesDialogBookingId(bookingId);
    setSubRoutesDialogLists((currentLists) => {
      if (currentLists[bookingId]) return currentLists;
      return {
        ...currentLists,
        [bookingId]: routeList,
      };
    });
    setSubRouteInput("");
    setSubRoutesDialogOpen(true);
  };

  const handleCloseSubRoutesDialog = () => {
    setSubRoutesDialogOpen(false);
  };

  const handleSubRoutesDialogDragEnd = (event) => {
    const operation = event.operation;
    if (!operation || operation.canceled || subRoutesDialogBookingId === null) {
      return;
    }

    setSubRoutesDialogLists((currentLists) => {
      const currentList = currentLists[subRoutesDialogBookingId] || [];
      let sourceIndex = -1;
      let targetIndex = -1;

      if (isSortableOperation(operation)) {
        sourceIndex = operation.source.initialIndex;
        targetIndex = operation.source.index;
      } else {
        const sourceId = String(operation.source?.id ?? "");
        const targetId = String(operation.target?.id ?? "");
        sourceIndex = currentList.findIndex(
          (route) => route.routeId === sourceId,
        );
        targetIndex = currentList.findIndex(
          (route) => route.routeId === targetId,
        );
      }

      if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex >= currentList.length ||
        targetIndex >= currentList.length ||
        sourceIndex === targetIndex
      ) {
        return currentLists;
      }

      return {
        ...currentLists,
        [subRoutesDialogBookingId]: arrayMove(
          currentList,
          sourceIndex,
          targetIndex,
        ),
      };
    });
  };

  const handleDeleteSubRouteDialog = (routeId) => {
    if (subRoutesDialogBookingId === null) return;

    setSubRoutesDialogLists((currentLists) => ({
      ...currentLists,
      [subRoutesDialogBookingId]: (
        currentLists[subRoutesDialogBookingId] || []
      ).filter((route) => route.routeId !== routeId),
    }));
  };

  const handleAddSubRouteDialog = (newValue) => {
    if (!newValue || subRoutesDialogBookingId === null) {
      return;
    }

    const newRoute = createDialogRoute(newValue, Date.now());
    setSubRoutesDialogLists((currentLists) => {
      const currentList = currentLists[subRoutesDialogBookingId] || [];
      const exists = currentList.some(
        (route) =>
          route.routeId === newRoute.routeId ||
          getNormalizedRouteName(route) === getNormalizedRouteName(newRoute),
      );

      if (exists) return currentLists;
      if (currentList.length >= MAX_SUB_ROUTES_DIALOG_ITEMS) {
        snackBarDialog("MAXIMO 10 RUTAS", "error");
        return currentLists;
      }

      return {
        ...currentLists,
        [subRoutesDialogBookingId]: [...currentList, newRoute],
      };
    });
    setSubRouteInput("");
  };

  const handleSelectSubRouteDialog = async (address) => {
    if (!address) return;

    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      handleAddSubRouteDialog({
        id: `google-${Date.now()}`,
        name: address,
        lat: latLng.lat,
        lng: latLng.lng,
      });
    } catch (error) {
      console.log(error);
      snackBarDialog("NO SE PUEDE AGREGAR LA RUTA", "error");
    }
  };

  const handleUpdateSubRoutesDialog = async () => {
    if (subRoutesDialogBookingId === null) return;

    const currentList = subRoutesDialogLists[subRoutesDialogBookingId] || [];
    const cleanList = currentList.filter((route) => route?.name);

    if (cleanList.length === 0) {
      snackBarDialog("Debe existir un destino", "error");
      return;
    }

    const destination = cleanList[cleanList.length - 1];
    const subRoutes = cleanList.slice(0, -1).map((route, index) => ({
      id: index + 1,
      name: route.name,
      lat: route.lat,
      lng: route.lng,
      url: route.url ?? route.url_address ?? "",
    }));
    const bookingSubRoutes = {
      id: subRoutesDialogBookingId,
      sub_routes: subRoutes,
      destination_name: destination.name,
      destination_lat: destination.lat,
      destination_lng: destination.lng,
    };

    try {
      setIsLoading(true);
      await updateBookingNowSubRoutes(bookingSubRoutes);
      setBooking((currentBooking) =>
        currentBooking.map((booking) =>
          booking.id === subRoutesDialogBookingId
            ? {
                ...booking,
                destination_name: bookingSubRoutes.destination_name,
                destination_lat: bookingSubRoutes.destination_lat,
                destination_lng: bookingSubRoutes.destination_lng,
                sub_routes: JSON.stringify(bookingSubRoutes.sub_routes),
              }
            : booking,
        ),
      );
      setSubRoutesDialogLists((currentLists) => ({
        ...currentLists,
        [subRoutesDialogBookingId]: cleanList.map((route, index) =>
          createDialogRoute(route, index + 1),
        ),
      }));
      snackBarDialog("RUTAS ACTUALIZADAS");
      handleCloseSubRoutesDialog();
    } catch (error) {
      console.log(error);
      snackBarDialog("NO SE PUEDE ACTUALIZAR LAS RUTAS", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const loadBookingNowRange = useCallback(
    async ({ start, end }) => {
      try {
        setIsLoading(true);
        const response = await getBookingNowV2({ start, end });
        const responseBookings = Object.values(response?.data || {});
        const requestedRows = end - start;

        setHasMoreBookings(responseBookings.length >= requestedRows);
        setBooking((currentBooking) => {
          const bookingById = new Map(
            currentBooking.map((booking) => [booking.id, booking]),
          );

          responseBookings.forEach((booking) => {
            const bookingCompanyId =
              booking.company_id ||
              booking.enterprise_company ||
              booking.companies_id;

            if (
              !bookingCompanyId ||
              Number(company_id) === Number(bookingCompanyId)
            ) {
              bookingById.set(booking.id, booking);
            }
          });

          const compareByDate = (a, b) => {
            const dateB = convertDate(b.date, b.time);
            const dateA = convertDate(a.date, a.time);
            return dateB - dateA;
          };

          return Array.from(bookingById.values()).sort(compareByDate);
        });
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    },
    [company_id],
  );

  useEffect(() => {
    loadBookingNowRange(INITIAL_RANGE);
  }, [loadBookingNowRange]);

  useEffect(() => {
    const getListClients = async () => {
      try {
        const token = getTokenRefresh();
        const response = await getClients(token);
        const clientList = response?.data?.data ?? response?.data ?? [];
        setClients(
          Array.isArray(clientList) ? clientList : Object.values(clientList),
        );
      } catch (error) {
        console.log(error);
      }
    };
    getListClients();
  }, []);

  useEffect(() => {
    const driversData = async () => {
      const verify = verifyExpireTokenRefresh();
      if (verify) return console.log("no token");

      const token = getTokenRefresh();
      try {
        const response = await getDriverAndVehicle(token);
        setDrivers(response?.data);
      } catch (error) {
        console.log(error);
      }
    };
    driversData();
  }, []);

  GridCellExpand.propTypes = {
    value: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
  };

  function renderCellExpand(params) {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={params.colDef.computedWidth}
      />
    );
  }

  renderCellExpand.propTypes = {
    colDef: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
  };

  const columns = [
    {
      headerName: "Pedido",
      field: "order_number",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 70,
    },
    {
      headerName: "Fecha", //aqui what para cliente
      field: "date",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 80,
      renderCell: ({
        row: {
          time,
          client_fullname,
          clientPhone,
          origin_name,
          destination_name,
          date,
          persons,
          driver_fullname,
          driver_phone,
          vehicle_bodywork,
          vehicle_plate,
          vehicle_color,
          vehicle_brand,
          notes,
        },
      }) => {
        const url = `https://api.whatsapp.com/send?phone=${clientPhone.trim()}&text=*Estimado Usuario:* ${client_fullname} ,%0A*Origen:* ${encodeURIComponent(
          origin_name,
        )}%0A*Destino:* ${encodeURIComponent(
          destination_name,
        )}%0A*Fecha:* ${date} - *Hora:* ${time}%0A*Pasajeros:* ${persons}%0A*Conductor Asignado:* ${driver_fullname}%0A*Model:* ${vehicle_brand}%0A*Carroceria:* ${vehicle_bodywork}%0A*Placa:* ${vehicle_plate}%0A*Color:* ${vehicle_color}%0A*NOTA:* ${notes}%0A*Telefono Conductor:* ${driver_phone}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {date}
          </a>
        );
      },
    },
    {
      headerName: "Hora",
      field: "time",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 80,
      renderCell: ({
        row: {
          time,
          client_fullname,
          clientPhone,
          origin_name,
          destination_name,
          date,
          persons,
          driver_fullname,
          driver_phone,
          vehicle_bodywork,
          vehicle_plate,
          vehicle_color,
          vehicle_brand,
          notes,
        },
      }) => {
        const url = `https://api.whatsapp.com/send?text=*Estimado Usuario:* ${client_fullname} ,%0A*Origen:* ${encodeURIComponent(
          origin_name,
        )}%0A*Destino:* ${encodeURIComponent(
          destination_name,
        )}%0A*Fecha:* ${date} - *Hora:* ${time}%0A*Pasajeros:* ${persons}%0A*Conductor Asignado:* ${driver_fullname}%0A*Model:* ${vehicle_brand}%0A*Carroceria:* ${vehicle_bodywork}%0A*Placa:* ${vehicle_plate}%0A*Color:* ${vehicle_color}%0A*NOTA:* ${notes}%0A*Telefono Conductor:* ${driver_phone}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {time}
          </a>
        );
      },
    },
    {
      headerName: "Nombre",
      field: "client_fullname",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 180,
      renderCell: ({ row: { client_fullname, clients_id, order_number } }) => {
        const url = `/admin/report-manifest/${clients_id}/${order_number}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {client_fullname}
          </a>
        );
      },
    },
    {
      headerName: "Origen",
      field: "origin_name",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
      renderCell: ({ row: { origin_name, origin_lat, origin_lng } }) => {
        const url = `https://maps.google.com/?q=${origin_lat},${origin_lng}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {origin_name}
          </a>
        );
      },
    },
    {
      headerName: "Destino",
      field: "destination_name",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
      renderCell: ({
        row: {
          id,
          origin_name,
          origin_lat,
          origin_lng,
          destination_name,
          destination_lat,
          destination_lng,
          sub_routes,
        },
      }) => {
        const subRoutes = getSubRoutesList(sub_routes);
        const origin = {
          name: origin_name,
          lat: origin_lat,
          lng: origin_lng,
        };
        const destination = {
          name: destination_name,
          lat: destination_lat,
          lng: destination_lng,
        };
        const destinationLink = (
          <Button
            type="button"
            variant="text"
            onClick={() =>
              handleOpenSubRoutesDialog(id, [], {
                name: destination_name,
                lat: destination_lat,
                lng: destination_lng,
              })
            }
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
              textTransform: "none",
              justifyContent: "flex-start",
              padding: 0,
              minWidth: 0,
            }}
          >
            {destination_name}
          </Button>
        );

        if (subRoutes.length === 0) return destinationLink;

        return (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() =>
                handleOpenSubRoutesDialog(id, subRoutes, destination)
              }
              sx={{
                minHeight: 28,
                px: 1,
                py: 0,
                fontSize: 11,
                textTransform: "none",
              }}
            >
              Paraderos
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() =>
                openGoogleMapsRoute(origin, subRoutes, destination)
              }
              sx={{
                minHeight: 28,
                px: 1,
                py: 0,
                fontSize: 11,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              Ver Ruta
            </Button>
          </Stack>
        );
      },
    },
    {
      headerName: "Pax",
      field: "persons",
      headerAlign: "left",
      align: "center",
      width: 20,
    },
    {
      headerName: "Notas",
      field: "notes",
      width: 200,
      renderCell: renderCellExpand,
    },
    {
      headerName: "Placa",
      field: "vehicle_plate",
      width: 90,
    },
    {
      headerName: "Tipo",
      field: "vehicle_bodywork",
      width: 90,
      renderCell: ({
        row: {
          time,
          clientPhone,
          client_fullname,
          origin_name,
          destination_name,
          date,
          persons,
          driver_fullname,
          driver_phone,
          vehicle_bodywork,
          vehicle_plate,
          vehicle_color,
          vehicle_brand,
          notes,
        },
      }) => {
        const url = `https://api.whatsapp.com/send?text=*Pasajero:* ${client_fullname} ,%0A*Origen:* ${encodeURIComponent(
          origin_name,
        )}%0A*Destino:* ${encodeURIComponent(
          destination_name,
        )}%0A*Fecha:* ${date} - *Hora:* ${time}%0A*Pasajeros:* ${persons}%0A*Conductor Asignado:* ${driver_fullname}%0A*Model:* ${vehicle_brand}%0A*Carroceria:* ${vehicle_bodywork}%0A*Placa:* ${vehicle_plate}%0A*Color:* ${vehicle_color}%0A*NOTA:* ${notes}%0A*Telefono Cliente:* ${clientPhone}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {vehicle_bodywork}
          </a>
        );
      },
    },
    {
      headerName: "Conductor",
      field: "driver_fullname",
      width: 200,
      renderCell: ({
        row: {
          time,
          clientPhone,
          client_fullname,
          origin_name,
          destination_name,
          date,
          persons,
          driver_fullname,
          driver_phone,
          vehicle_bodywork,
          vehicle_plate,
          vehicle_color,
          vehicle_brand,
          notes,
        },
      }) => {
        const url = `https://api.whatsapp.com/send?phone=${driver_phone}&text=*Pasajero:* ${client_fullname} ,%0A*Origen:* ${encodeURIComponent(
          origin_name,
        )}%0A*Destino:* ${encodeURIComponent(
          destination_name,
        )}%0A*Fecha:* ${date} - *Hora:* ${time}%0A*Pasajeros:* ${persons}%0A*Conductor Asignado:* ${driver_fullname}%0A*Model:* ${vehicle_brand}%0A*Carroceria:* ${vehicle_bodywork}%0A*Placa:* ${vehicle_plate}%0A*Color:* ${vehicle_color}%0A*NOTA:* ${notes}%0A*Telefono Cliente:* ${clientPhone}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {driver_fullname}
          </a>
        );
      },
    },
    {
      headerName: "Impuesto",
      field: "payment_tax",
      width: 90,
    },
    {
      headerName: "Peaje",
      field: "payment_toll",
      width: 90,
    },
    {
      headerName: "Precio C.",
      field: "payment_price_driver",
      width: 90,
    },
    {
      headerName: "TOTAL",
      field: "payment_price_total",
      width: 90,
    },
    {
      headerName: "Estado",
      field: "state_payment",
      width: 90,
    },
    {
      headerName: "Creado",
      field: "created_by",
      width: 80,
    },
    {
      headerName: "SERVICIO",
      field: "travel_status_id",
      width: 100,
      renderCell: ({ row: { travel_status_id } }) => {
        return (
          <a
            style={{
              color: "white",
              fontSize: 13,
              listStyle: "none",
              padding: "5px",
              textDecoration: "none",
              background: travel_status_id == 2 ? "blue" : "red",
            }}
          >
            {travelStatus[travel_status_id]}
          </a>
        );
      },
    },
    {
      headerName: "",
      field: "actions",
      width: 90,
      renderCell: ({ row: { id } }) => {
        return (
          <div>
            <Button
              color="secondary"
              aria-controls={open ? "fade-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={(event) => {
                setUID(id);
                setAnchorEl(event.currentTarget);
              }}
            >
              OPCIONES
            </Button>
            <Menu
              id="menu-options"
              MenuListProps={{ "aria-labelledby": "fade-button" }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              TransitionComponent={Fade}
            >
              <MenuItem
                onClick={() => {
                  handleOpenEditModal();
                  setBookingBusinessData();
                  handleClose();
                }}
              >
                VER
              </MenuItem>

              <MenuItem
                onClick={async () => {
                  const booking = {
                    id: UID,
                    discount: 0,
                    tax: 0,
                    toll: 0,
                    price_driver: 0,
                    price_total: 0,
                    commission: 0,
                    status: "PAGADO",
                  };
                  try {
                    const token = "";
                    await updateStatusBooking(token, booking);
                    snackBarDialog("PEDIDO PAGADO");
                    handleClose();
                  } catch (error) {
                    snackBarDialog("ERROR: NO SE PAGO EL PEDIDO", "error");
                    handleClose();
                    console.log(error);
                  }
                }}
              >
                PAGADO
              </MenuItem>

              <MenuItem
                onClick={async () => {
                  const booking = {
                    id: UID,
                    discount: 0,
                    tax: 0,
                    toll: 0,
                    price_driver: 0,
                    price_total: 0,
                    commission: 0,
                    status: "CREDITO",
                  };
                  try {
                    const token = "";
                    await updateStatusBooking(token, booking);
                    snackBarDialog("PEDIDO CREDITO");
                    handleClose();
                  } catch (error) {
                    snackBarDialog("ERROR: NO SE PAGO EL PEDIDO", "error");
                    handleClose();
                    console.log(error);
                  }
                }}
              >
                CREDITO
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  const booking = {
                    id: UID,
                    discount: 0,
                    tax: 0,
                    toll: 0,
                    price_driver: 0,
                    price_total: 0,
                    commission: 0,
                    status: "CANCELADO",
                  };
                  try {
                    const token = "";
                    await updateStatusBooking(token, booking);
                    snackBarDialog("PEDIDO CANCELADO");
                    handleClose();
                  } catch (error) {
                    snackBarDialog("ERROR: NO SE PAGO EL PEDIDO", "error");
                    handleClose();
                    console.log(error);
                  }
                }}
              >
                CANCELADO
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  try {
                    await updateBookingTravelStatus(UID, 3);
                    snackBarDialog("PEDIDO FINALIZADO");
                    handleClose();
                  } catch (error) {
                    snackBarDialog("ERROR: NO SE PAGO EL PEDIDO", "error");
                    handleClose();
                    console.log(error);
                  }
                }}
              >
                FINALIZADO
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };
  const handleCloseSnk = () => setStateSnk(false);

  //MODAL
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectBooking, setSelectBooking] = useState(null);
  const handleOpenEditModal = () => setOpenEditModal(true);
  const handleCloseEditModal = () => setOpenEditModal(false);

  const [advanceList, setAdvanceList] = useState([]);

  const setBookingBusinessData = async () => {
    const order = booking.filter((book) => {
      if (book.id === UID) return true;
      return false;
    });

    if (order.length > 0) {
      setSelectBooking(order[0]);
      const resp = await getPaymentsList(order[0].order_number);
      console.log("advanceList", resp);
      setAdvanceList(resp.data.resp);
    }
    console.log(order);
  };

  const getPaymentsList = async (orderNumber) => {
    const resp = await getAdvanceCashOrder(orderNumber);
    return resp;
  };

  const styleModal = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "1px solid #000",
    boxShadow: 20,
    width: 300,
    p: 2,
  };

  const [igv, setIgv] = useState(0);

  const styles = StyleSheet.create({
    page: {
      fontFamily: "Helvetica",
      fontSize: 11,
      paddingTop: 30,
      paddingLeft: 20,
      paddingRight: 20,
      paddingBottom: 35,
      lineHeight: 1.5,
      flexDirection: "column",
    },
  });

  const [dataSelected, setDataSelected] = useState([]);
  const [tableData, setTableData] = useState(null);
  const [pdfFormat, setPdfFormat] = useState("vertical");

  const getAdvanceCashItems = async (orderNumber) => {
    const resp = await getAdvanceCashOrder(orderNumber);
    return resp;
  };

  useEffect(() => {
    if (dataSelected.length > 0) {
      const promises = Object.values(dataSelected).map(async (book) => {
        const advanceList = await getAdvanceCashItems(book.order_number);
        let advance = 0;
        if (advanceList.data.resp.length > 0) {
          advanceList.data.resp.forEach((adv) => {
            advance += Number(adv.amount);
          });
        }

        const total = Number(book.payment_price_total);
        const advanceAmount = advance;
        const discountAmount = Number(book.payment_discount);

        return { total, advance: advanceAmount, discount: discountAmount };
      });

      Promise.all(promises).then((values) => {
        let total = 0;
        let advance = 0;
        let discount = 0;

        values.forEach(
          ({
            total: bookTotal,
            advance: bookAdvance,
            discount: bookDiscount,
          }) => {
            total += Number(bookTotal);
            advance += Number(bookAdvance);
            discount += Number(bookDiscount);
          },
        );

        setTableData({
          id: "5df3180a09ea16dc4b95f910",
          invoice_no: "201906-28",
          company: "TAXIMUNDO",
          email: "info@taximundo.com",
          phone: "(+51) 54 200 900 - (+51) 958 200 900 - (+51) 951 200 900",
          address: "Servicios Generales Trans Mundo E.I.R.L.",
          items: dataSelected,
          igv: 0,
          total: total,
          discount,
          advance,
          peaje: 0,
          subTotal: 0,
          from: 0,
          to: 0,
        });
      });
    } else {
      setTableData(null);
    }

  }, [dataSelected]);

  const listState = ["PENDIENTE", "PAGADO", "CANCELADO"];
  const [driverStatus, setDriverStatus] = useState("");
  const rows = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;

    return booking.slice(start, end);
  }, [booking, page, pageSize]);
  const horizontalTableData = useMemo(() => {
    if (!tableData) return null;

    const items = dataSelected.map((book) => ({
      ...book,
      fullname_employee:
        book.fullname_employee ||
        book.client_fullname ||
        `${book.client_name ?? ""} ${book.client_lastname ?? ""}`.trim(),
      toll: Number(book.toll ?? book.payment_toll ?? 0),
      price_total: Number(book.price_total ?? book.payment_price_total ?? 0),
      other_name: book.other_name ?? book.otherPersonName ?? "",
      destination_name: getDestinationText(book),
    }));
    const peaje = items.reduce((total, book) => total + Number(book.toll), 0);

    return {
      ...tableData,
      items,
      peaje,
      igv: Number(tableData.igv ?? 0),
      subTotal: Number(tableData.subTotal ?? 0),
      total: Number(tableData.total ?? 0),
      from: items.length > 0 ? items[0].date : "",
      to: items.length > 0 ? items[items.length - 1].date : "",
    };
  }, [dataSelected, tableData]);
  const activeSubRoutesDialogList =
    subRoutesDialogLists[subRoutesDialogBookingId] || [];
  const rowCount = hasMoreBookings
    ? Math.max(booking.length + pageSize, (page + 2) * pageSize)
    : booking.length;
  const handlePageChange = (nextPage) => {
    const start = nextPage * pageSize;
    const end = start + pageSize;

    setPage(nextPage);
    loadBookingNowRange({ start, end });
  };
  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(0);
    loadBookingNowRange({ start: 0, end: nextPageSize });
  };

  return (
    <div>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="10px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid container spacing={1}>
                <Grid item xs={12} sm={7}>
                  <Header title="HISTORIAL DE PEDIDOS" subtitle="" />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Stack
                    direction="row"
                    spacing={{ xs: 0.5, sm: 1 }}
                    alignItems="center"
                    justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                    sx={{
                      height: "100%",
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      py: { xs: 0.5, sm: 0 },
                    }}
                  >
                    {pdfFormat === "vertical" && (
                      <PDFDownloadLink
                        document={
                          <Document>
                            {dataSelected.length > 0 && tableData != null && (
                              <Page size="A4" style={styles.page}>
                                <HeaderM />
                                {/* <Title title={"Taxi Mundo PERU"} /> */}
                                <Contact invoice={tableData} />
                                <DocumentName
                                  title={"RESERVAS"}
                                  from={"16/11/2023"}
                                  to={"16/11/2023"}
                                />
                                <TableBooking invoice={tableData} />
                              </Page>
                            )}
                          </Document>
                        }
                        fileName={"detalles-cotizacion-taximundo.pdf"}
                        style={headerPdfLinkSx}
                      >
                        <Button
                          variant="contained"
                          color="success"
                          sx={headerActionButtonSx}
                        >
                          DESCARGAR
                        </Button>
                      </PDFDownloadLink>
                    )}
                    {pdfFormat === "horizontal" && (
                      <PDFDownloadLink
                        document={
                          <Document>
                            {dataSelected.length > 0 && horizontalTableData && (
                              <Page size={[841.89, 595.28]} style={styles.page}>
                                <ReportTitle title="Taxi Mundo PERU" />
                                <ReportContact invoice={horizontalTableData} />
                                <ReportDocumentName
                                  title={"DETALLE DE SERVICIOS"}
                                  from={horizontalTableData.from}
                                  to={horizontalTableData.to}
                                />
                                <ReportTableHorizontal
                                  invoice={horizontalTableData}
                                />
                              </Page>
                            )}
                          </Document>
                        }
                        fileName={"detalles-cotizacion-taximundo.pdf"}
                        style={headerPdfLinkSx}
                      >
                        <Button
                          variant="contained"
                          color="success"
                          sx={headerActionButtonSx}
                        >
                          DESCARGAR
                        </Button>
                      </PDFDownloadLink>
                    )}
                    {dataSelected.length > 0 && tableData != null && (
                      <Box
                        sx={{
                          flexShrink: 0,
                          "& a": headerPdfLinkSx,
                          "& .MuiButton-root": headerActionButtonSx,
                        }}
                      >
                        <VoucherHistory data={tableData} />
                      </Box>
                    )}
                    <FormControl sx={{ flexShrink: 0 }}>
                      <RadioGroup
                        row
                        sx={{
                          alignItems: "center",
                          flexWrap: "nowrap",
                          gap: { xs: 0.5, sm: 0.75 },
                        }}
                        name="bookingNowPdfFormat"
                        value={pdfFormat}
                        onChange={(event) => setPdfFormat(event.target.value)}
                      >
                        <FormControlLabel
                          value="horizontal"
                          control={<Radio color="success" />}
                          label="Horizontal"
                          sx={getRadioOptionSx(pdfFormat, "horizontal")}
                        />
                        <FormControlLabel
                          value="vertical"
                          control={<Radio color="success" />}
                          label="Vertical"
                          sx={getRadioOptionSx(pdfFormat, "vertical")}
                        />
                      </RadioGroup>
                    </FormControl>
                    <Button
                      type="button"
                      variant="contained"
                      color="secondary"
                      sx={headerActionButtonSx}
                      onClick={handleOpenFilterDialog}
                    >
                      Filtrar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* </Box> */}
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 2">
                <Box
                  m="10px 0 0 0"
                  height="100vh"
                  sx={{
                    "& .MuiDataGrid-root": {
                      border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "none",
                    },
                    "& .name-column--cell": {
                      color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                      color: `${colors.greenAccent[200]} !important`,
                    },
                  }}
                >
                  <DataGrid
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    pagination
                    paginationMode="server"
                    page={page}
                    pageSize={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    rowCount={rowCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    loading={isLoading}
                    localeText={
                      esES.components.MuiDataGrid.defaultProps.localeText
                    }
                    checkboxSelection
                    onSelectionModelChange={(ids) => {
                      setDataSelected([]);
                      ids.map((id) => {
                        const select = booking.find((row) => row.id === id);
                        setDataSelected((oldData) => [...oldData, select]);
                      });
                    }}
                  />
                </Box>
                {/* <TableBookNow type_booking={1} /> */}
              </Box>
            </Box>

            <Dialog
              open={filterDialogOpen}
              onClose={handleCloseFilterDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Filtrar</DialogTitle>
              <DialogContent>
                <FormControl fullWidth>
                  <InputLabel id="booking-now-filter-label">Filtro</InputLabel>
                  <Select
                    labelId="booking-now-filter-label"
                    id="booking-now-filter"
                    value={filterOption}
                    label="Filtro"
                    onChange={handleFilterOptionChange}
                  >
                    <MenuItem value="numeroPerdido">Nro Pedido</MenuItem>
                    <MenuItem value="origen">Origen</MenuItem>
                    <MenuItem value="destino">Destino</MenuItem>
                    <MenuItem value="notas">Notas</MenuItem>
                    <MenuItem value="fecha">Fecha</MenuItem>
                    <MenuItem value="cliente">Cliente</MenuItem>
                    <MenuItem value="conductor">Conductor</MenuItem>
                  </Select>
                </FormControl>
                <Box mt={2}>
                  {showFilterTextField && (
                    <TextField
                      fullWidth
                      label={filterInputLabel[filterOption]}
                      value={filterTextValue}
                      onChange={(event) =>
                        setFilterTextValue(event.target.value)
                      }
                    />
                  )}
                  {showNotesFilterMode && (
                    <Box>
                      <FormControl fullWidth>
                        <RadioGroup
                          row
                          name="bookingNowFilterNotesMode"
                          value={filterNotesMode}
                          onChange={(event) => {
                            setFilterNotesMode(event.target.value);
                            setFilterTextValue("");
                            setFilterNoResults(false);
                          }}
                          sx={centeredRadioGroupSx}
                        >
                          <FormControlLabel
                            value="general"
                            control={<Radio color="secondary" />}
                            label="General"
                            sx={getRadioOptionSx(filterNotesMode, "general")}
                          />
                          <FormControlLabel
                            value="admin"
                            control={<Radio color="secondary" />}
                            label="Admin"
                            sx={getRadioOptionSx(filterNotesMode, "admin")}
                          />
                        </RadioGroup>
                      </FormControl>
                      <TextField
                        fullWidth
                        label={
                          filterNotesMode === "admin" ? "Notas Admin" : "Notas"
                        }
                        value={filterTextValue}
                        onChange={(event) =>
                          setFilterTextValue(event.target.value)
                        }
                      />
                    </Box>
                  )}
                  {showFilterDatePicker && (
                    <Box>
                      <FormControl fullWidth>
                        <RadioGroup
                          row
                          name="bookingNowFilterDateMode"
                          value={filterDateMode}
                          onChange={(event) =>
                            setFilterDateMode(event.target.value)
                          }
                          sx={centeredRadioGroupSx}
                        >
                          <FormControlLabel
                            value="fecha"
                            control={<Radio color="secondary" />}
                            label="Fecha"
                            sx={getRadioOptionSx(filterDateMode, "fecha")}
                          />
                          <FormControlLabel
                            value="rango"
                            control={<Radio color="secondary" />}
                            label="Rango"
                            sx={getRadioOptionSx(filterDateMode, "rango")}
                          />
                        </RadioGroup>
                      </FormControl>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box
                          display="grid"
                          gridTemplateColumns={{
                            xs: "1fr",
                            sm:
                              filterDateMode === "rango"
                                ? "repeat(2, minmax(0, 1fr))"
                                : "1fr",
                          }}
                          gap={2}
                        >
                          <MobileDatePicker
                            label={
                              filterDateMode === "rango"
                                ? "Fecha inicio"
                                : "Fecha"
                            }
                            inputFormat="DD/MM/YYYY"
                            value={filterDateStartValue}
                            onChange={(newValue) =>
                              setFilterDateStartValue(newValue)
                            }
                            renderInput={(params) => (
                              <TextField {...params} fullWidth />
                            )}
                          />
                          {filterDateMode === "rango" && (
                            <MobileDatePicker
                              label="Fecha fin"
                              inputFormat="DD/MM/YYYY"
                              value={filterDateEndValue}
                              onChange={(newValue) =>
                                setFilterDateEndValue(newValue)
                              }
                              renderInput={(params) => (
                                <TextField {...params} fullWidth />
                              )}
                            />
                          )}
                        </Box>
                      </LocalizationProvider>
                    </Box>
                  )}
                  {showRouteFilterMode && (
                    <FormControl fullWidth>
                      <RadioGroup
                        row
                        name="bookingNowFilterRouteMode"
                        value={filterRouteMode}
                        onChange={(event) => {
                          setFilterRouteMode(event.target.value);
                          setFilterTextValue("");
                          setFilterAutocompleteValue(null);
                          setFilterNoResults(false);
                        }}
                        sx={centeredRadioGroupSx}
                      >
                        <FormControlLabel
                          value="registrado"
                          control={<Radio color="secondary" />}
                          label="Registrado"
                          sx={getRadioOptionSx(filterRouteMode, "registrado")}
                        />
                        <FormControlLabel
                          value="libre"
                          control={<Radio color="secondary" />}
                          label="Libre"
                          sx={getRadioOptionSx(filterRouteMode, "libre")}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                  {showDriverFilterMode && (
                    <FormControl fullWidth>
                      <RadioGroup
                        row
                        name="bookingNowFilterDriverMode"
                        value={filterDriverMode}
                        onChange={(event) => {
                          setFilterDriverMode(event.target.value);
                          setFilterTextValue("");
                          setFilterAutocompleteValue(null);
                          setFilterNoResults(false);
                        }}
                        sx={centeredRadioGroupSx}
                      >
                        <FormControlLabel
                          value="nombre"
                          control={<Radio color="secondary" />}
                          label="Nombre"
                          sx={getRadioOptionSx(filterDriverMode, "nombre")}
                        />
                        <FormControlLabel
                          value="placa"
                          control={<Radio color="secondary" />}
                          label="Placa"
                          sx={getRadioOptionSx(filterDriverMode, "placa")}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                  {showRouteFilterMode && filterRouteMode === "libre" && (
                    <TextField
                      fullWidth
                      label={filterInputLabel[filterOption]}
                      value={filterTextValue}
                      onChange={(event) =>
                        setFilterTextValue(event.target.value)
                      }
                    />
                  )}
                  {showDriverFilterMode && filterDriverMode === "placa" && (
                    <TextField
                      fullWidth
                      label="Placa"
                      value={filterTextValue}
                      onChange={(event) =>
                        setFilterTextValue(event.target.value)
                      }
                    />
                  )}
                  {showFilterAutocomplete &&
                    (!showRouteFilterMode ||
                      filterRouteMode === "registrado") &&
                    (!showDriverFilterMode ||
                      filterDriverMode === "nombre") && (
                      <Autocomplete
                        fullWidth
                        options={filterAutocompleteOptions}
                        value={filterAutocompleteValue}
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;

                          if (filterOption === "cliente") {
                            return `${option?.name ?? ""} ${
                              option?.lastname ?? ""
                            }${
                              option?.phone ? ` - ${option.phone}` : ""
                            }`.trim();
                          }

                          if (filterOption === "conductor") {
                            return `${option?.name ?? ""} ${
                              option?.lastname ?? ""
                            }${
                              option?.dni ? ` - DNI: ${option.dni}` : ""
                            }`.trim();
                          }

                          return option?.name ?? "";
                        }}
                        onChange={(event, newValue) =>
                          setFilterAutocompleteValue(newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filterInputLabel[filterOption]}
                          />
                        )}
                      />
                    )}
                  {filterNoResults && (
                    <Typography color="error" mt={2} fontWeight={600}>
                      NO SE ENCONTRO NINGUNA COINCIDENCIA
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  onClick={handleCloseFilterDialog}
                >
                  Cerrar
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={handleSearchFilter}
                >
                  Buscar
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={subRoutesDialogOpen}
              onClose={handleCloseSubRoutesDialog}
              fullWidth
              maxWidth="xs"
              PaperProps={{ sx: { height: "80vh" } }}
            >
              <DialogTitle>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                >
                  <Typography variant="h6" sx={{ flexShrink: 0 }}>
                    Paraderos
                  </Typography>
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <PlacesAutocomplete
                      value={subRouteInput}
                      onChange={setSubRouteInput}
                      onSelect={handleSelectSubRouteDialog}
                      searchOptions={{
                        componentRestrictions: { country: "pe" },
                      }}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <Box sx={{ position: "relative" }}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Agregar ruta"
                            color="success"
                            {...getInputProps({
                              name: "subRouteInput",
                            })}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 2,
                              bgcolor: "background.paper",
                              boxShadow: 3,
                            }}
                          >
                            {loading && (
                              <Box sx={{ px: 1, py: 0.75 }}>Loading...</Box>
                            )}
                            {suggestions.map((suggestion) => (
                              <Box
                                key={suggestion.description}
                                {...getSuggestionItemProps(suggestion, {
                                  style: {
                                    cursor: "pointer",
                                    padding: "8px",
                                    backgroundColor: suggestion.active
                                      ? "rgba(20, 83, 45, 0.12)"
                                      : "#fff",
                                    color: "#000",
                                  },
                                })}
                              >
                                {suggestion.description}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </PlacesAutocomplete>
                  </Box>
                </Stack>
              </DialogTitle>
              <DialogContent>
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <DragDropProvider onDragEnd={handleSubRoutesDialogDragEnd}>
                    <Stack spacing={1}>
                      {activeSubRoutesDialogList.map((route, index) => (
                        <SubRouteSortableItem
                          key={route.routeId}
                          route={route}
                          index={index}
                          onDelete={handleDeleteSubRouteDialog}
                        />
                      ))}
                    </Stack>
                  </DragDropProvider>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  type="button"
                  variant="contained"
                  color="success"
                  onClick={handleUpdateSubRoutesDialog}
                  disabled={isLoading}
                >
                  Actualizar
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  onClick={handleCloseSubRoutesDialog}
                >
                  Cerrar
                </Button>
              </DialogActions>
            </Dialog>

            <Modal open={openEditModal} onClose={handleCloseEditModal}>
              <Box
                sx={{ ...styleModal, height: "95vh", overflow: "auto" }}
                backgroundColor={colors.primary[400]}
              >
                {selectBooking && (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="h5" component="h2">
                        PEDIDO :
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6}>
                      <TextField
                        value={selectBooking.order_number}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label={"NRO PEDIDO"}
                        name="order_number"
                      />
                    </Grid>

                    <Grid item xs={6} sm={6} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id="select-type">CONDUCTOR</InputLabel>
                        <Select
                          value={selectBooking.payment_type}
                          fullWidth
                          labelId="select-type"
                          id="payment_type"
                          onChange={(e) => {
                            selectBooking.payment_type = e.target.value;
                            setIgv(e.target.value);
                          }}
                          label="CONDUCTOR"
                          name="payment_type"
                        >
                          <MenuItem value={1}>EFECTIVO</MenuItem>
                          <MenuItem value={2}>CREDITO</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={3} md={6}>
                      <TextField
                        value={selectBooking.payment_commission}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="number"
                        label={"COMISION"}
                        name="payment_commission"
                        onChange={(e) => {
                          selectBooking.payment_commission = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={3} md={6}>
                      <TextField
                        value={selectBooking.payment_tax}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="number"
                        label={"IMPUESTO"}
                        name="payment_tax"
                        onChange={(e) => {
                          selectBooking.payment_tax = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={3} md={6}>
                      <TextField
                        value={selectBooking.payment_toll}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="number"
                        label={"PEAJE"}
                        name="payment_toll"
                        onChange={(e) => {
                          selectBooking.payment_toll = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={3} md={6}>
                      <TextField
                        value={selectBooking.payment_price_driver}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="number"
                        label={"PRECIO C."}
                        name="payment_price_driver"
                        onChange={(e) => {
                          selectBooking.payment_price_driver = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={3} md={6}>
                      <TextField
                        value={selectBooking.payment_price_total}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="number"
                        label={"TOTAL"}
                        name="payment_price_total"
                        onChange={(e) => {
                          selectBooking.payment_price_total = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={3} sm={6} md={6}>
                      <TextField
                        value={advance}
                        name="advance"
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="number"
                        label="ADELANTO"
                        onChange={(e) => setAdvance(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <Checkbox
                              style={{ marginRight: -15 }}
                              checked={otherPay}
                              onChange={handleOtherPay}
                              color="success"
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        value={
                          driverStatus === ""
                            ? selectBooking.status
                            : driverStatus
                        }
                        disablePortal
                        id="combo-box-driver-status"
                        onChange={(e, newValue) => {
                          if (newValue != null) setDriverStatus(newValue);
                        }}
                        name="driverStatus"
                        options={listState}
                        getOptionLabel={(option) => `${option}`}
                        renderInput={(params) => (
                          <TextField {...params} label={"ESTADO"} />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {drivers && (
                        <Autocomplete
                          value={driverSelect}
                          disablePortal
                          id="combo-box-driver"
                          onChange={(e, newValue) => {
                            setDriverSelect(newValue);
                          }}
                          options={drivers}
                          getOptionLabel={(option) =>
                            `${option.name} ${option.lastname} ${option.brand} ${option.model} ${option.bodywork} ${option.plate} ${option.color}`
                          }
                          renderInput={(params) => (
                            <TextField {...params} label={"Conductor"} />
                          )}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextField
                        value={selectBooking.notes}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label={"NOTAS"}
                        name="notes"
                        onChange={(e) => {
                          selectBooking.notes = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextField
                        value={selectBooking.notes_admin}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label={"NOTAS ADMIN"}
                        name="notesAdmin"
                        onChange={(e) => {
                          selectBooking.notes_admin = e.target.value;
                          setIgv(e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <div>
                        {advanceList.map((pay, idx) => {
                          return (
                            <Fragment key={idx}>
                              <span style={{ fontWeight: "bold" }}>
                                {pay.created_by}
                              </span>
                              {", "}
                              <span style={{ fontWeight: "bold" }}>Pago: </span>
                              {pay.amount} {pay.currency},{" "}
                              <span style={{ fontWeight: "bold" }}>
                                Fecha:{" "}
                              </span>
                              {pay.operation_date}
                              {". "}
                            </Fragment>
                          );
                        })}
                      </div>
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        sx={{ height: 40 }}
                        disabled={isLoading}
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            //UPDATE los nuevos datos
                            setIsLoading(true);
                            const book = {
                              id: selectBooking.id,
                              order_number: selectBooking.order_number,
                              payment_type_id: selectBooking.payment_type,
                              commission: selectBooking.payment_commission,
                              tax: selectBooking.payment_tax,
                              toll: selectBooking.payment_toll,
                              driver: selectBooking.payment_price_driver,
                              total: selectBooking.payment_price_total,
                              status:
                                driverStatus === ""
                                  ? selectBooking.status
                                  : driverStatus,
                              driver_id: driverSelect
                                ? driverSelect.driver_id
                                : selectBooking.drivers_id,
                              vehicle_id: driverSelect
                                ? driverSelect.vehicle_id
                                : selectBooking.vehicles_id,
                              notes: selectBooking.notes,
                              notes_admin: selectBooking.notes_admin,
                            };

                            if (otherPay) {
                              const currencyList = {
                                1: "PEN",
                                2: "USD",
                              };
                              const advReg = {
                                orderNumber: selectBooking.order_number,
                                transactionId: `${
                                  selectBooking.order_number
                                }-${Date.now()}`,
                                currency:
                                  currencyList[selectBooking.payment_currency],
                                amount: advance,
                                operationDate: datePay,
                                status: "completed",
                              };
                              const resp = await createAdvanceCash(advReg);
                              console.log(resp.data);
                            }

                            await updatePaymentBookingNow("token", book);
                            snackBarDialog("DATOS ACTUALIZADOS");
                            handleCloseEditModal();
                            setIsLoading(false);
                          } catch (error) {
                            setIsLoading(false);
                            snackBarDialog(
                              "NO SE PUEDE ACTUALIZAR LOS DATOS",
                              "error",
                            );
                            console.log(error);
                            handleCloseEditModal();
                          }
                        }}
                      >
                        GUARDAR
                      </Button>
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        sx={{ height: 40 }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleCloseEditModal();
                        }}
                      >
                        CANCELAR
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Modal>

            <SnackBar
              stateSnk={stateSnk}
              vertical={vertical}
              horizontal={horizontal}
              duration={4000}
              handleClose={handleCloseSnk}
              severity={severity}
              messageSnk={messageSnk}
            />
          </Box>
        </main>
      </div>
    </div>
  );
};

export default HistoryListBookingNow;
